/// <reference types="google.maps" />
import * as React from 'react';
import { InfoWindow, useMap } from '@vis.gl/react-google-maps';
import { icons } from '@koob/ui';
import { popupPan } from './camera';

const PopupCloseContext = React.createContext<() => void>(() => undefined);

/** Closes the popup this content sits in (after an action, say). */
export const useKoobMapPopupClose = () => React.useContext(PopupCloseContext);

type Props = {
  /** Pin the popup opens from; takes precedence over lat/lng. */
  anchor?: google.maps.marker.AdvancedMarkerElement | null;
  lat?: number | string;
  lng?: number | string;
  maxWidth?: number;
  /** Pages through pins on the same spot: "‹ 2 / 5 ›" above the content. */
  pager?: { index: number; count: number; onChange: (index: number) => void };
  onClose: () => void;
  children: React.ReactNode;
};

// A popup at a group's position, not on a pin, clears the group bubble.
const GROUP_OFFSET: [number, number] = [0, -18];

const roundButton =
  'flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border-none text-gray-700 hover:text-gray-900 disabled:cursor-default disabled:opacity-30';

/**
 * A KOOB popup on the map: borderless card, own close button. Content
 * brings its own padding (cards usually do; plain text should add some).
 */
export function KoobMapPopup({
  anchor,
  lat,
  lng,
  maxWidth = 280,
  pager,
  onClose,
  children,
}: Props) {
  const map = useMap();
  // State, not a ref: InfoWindow mounts its content a render later.
  const [content, setContent] = React.useState<HTMLDivElement | null>(null);
  const position = { lat: parseFloat(`${lat}`), lng: parseFloat(`${lng}`) };
  const hasPosition =
    Number.isFinite(position.lat) && Number.isFinite(position.lng);

  // Google's auto-pan measures the popup before its content renders and stops
  // short, cutting off the top. Pan ourselves once Google has placed the popup
  // (it holds still for a frame), and again when its size changes (paging).
  React.useEffect(() => {
    if (!map || !content) {
      return undefined;
    }
    // ponytail: gives up after a second, the popup then stays where it is.
    let frame = 0;
    let idle: google.maps.MapsEventListener | undefined;
    // Measured while the popup still settles, a pan can fall short: check
    // again once the map stops, a few times at most.
    let pansLeft = 3;
    const pan = () => {
      const box = content.closest('.gm-style-iw-c')?.getBoundingClientRect();
      if (!box) {
        return;
      }
      const [x, y] = popupPan(box, map.getDiv().getBoundingClientRect());
      if ((x || y) && pansLeft > 0) {
        pansLeft -= 1;
        idle = google.maps.event.addListenerOnce(map, 'idle', pan);
        map.panBy(x, y);
      }
    };
    const fit = (last = '', framesLeft = 60) => {
      const box = content.closest('.gm-style-iw-c')?.getBoundingClientRect();
      const key = box ? `${box.top},${box.left},${box.height}` : '';
      if (box && key === last) {
        pan();
      } else if (framesLeft > 0) {
        frame = requestAnimationFrame(() => fit(key, framesLeft - 1));
      }
    };
    fit();
    let height: number | null = null;
    const observer = new ResizeObserver(([entry]) => {
      const next = entry.contentRect.height;
      if (height !== null && next !== height) {
        cancelAnimationFrame(frame);
        pansLeft = 3;
        fit();
      }
      height = next;
    });
    observer.observe(content);

    return () => {
      cancelAnimationFrame(frame);
      idle?.remove();
      observer.disconnect();
    };
  }, [map, content]);

  if (!anchor && !hasPosition) {
    return null;
  }

  return (
    <InfoWindow
      anchor={anchor ?? undefined}
      position={anchor ? undefined : position}
      headerDisabled
      disableAutoPan
      maxWidth={maxWidth}
      pixelOffset={anchor ? undefined : GROUP_OFFSET}
      onCloseClick={onClose}
      // The only height cap (Google's is off, see KoobMapsProvider): the map's
      // height minus the pin, so the card shrinks to fit a short map.
      className="flex max-h-[calc(100cqh-72px)] flex-col"
    >
      <div ref={setContent} className="relative flex max-h-[inherit] flex-col">
        {pager && (
          <div className="flex h-11 shrink-0 items-center gap-1 border-0 border-b border-solid border-gray-100 bg-white pl-2 pr-11">
            <button
              type="button"
              aria-label="Previous"
              disabled={pager.index === 0}
              onClick={() => pager.onChange(pager.index - 1)}
              className={`${roundButton} bg-transparent hover:bg-gray-100`}
            >
              <i className={`${icons.chevronLeft} text-xs`} />
            </button>
            <span className="min-w-[44px] text-center text-xs font-medium tabular-nums text-gray-600">
              {pager.index + 1} / {pager.count}
            </span>
            <button
              type="button"
              aria-label="Next"
              disabled={pager.index === pager.count - 1}
              onClick={() => pager.onChange(pager.index + 1)}
              className={`${roundButton} bg-transparent hover:bg-gray-100`}
            >
              <i className={`${icons.chevronRight} text-xs`} />
            </button>
          </div>
        )}
        <PopupCloseContext.Provider value={onClose}>
          {children}
        </PopupCloseContext.Provider>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className={`${roundButton} absolute right-2 top-2 bg-white/90 shadow-sm hover:bg-white`}
        >
          <i className={icons.close} />
        </button>
      </div>
    </InfoWindow>
  );
}
